import { type Database } from '@databases/model/database';
import { FieldType } from '@databases/model/field';

import {
  serializeDatabaseContentsInBatches,
  serializeDatabaseContents,
  serializeSelectedCells,
  type SelectedCells,
  type JiraIssueDetailsResolver,
  type ConfluencePageConnectionResolver,
  type DatabaseEntryFieldValuesSnapshot
} from './contents.ts';
import { serializeDatabaseSchema } from './schema.ts';
import { serializeDatabaseViews } from './view.ts';
import { Warnings } from './warnings.ts';

export type {
  SelectedCells,
  JiraIssueDetailsResolver,
  ConfluencePageConnectionResolver
} from './contents.ts';
export { createSelectedCells } from './contents.ts';

export type SerializeDatabaseOptions = {
  currentViewId?: string;
  entryIds?: readonly string[];
  fieldIds?: readonly string[];
  resolveJiraIssueDetails?: JiraIssueDetailsResolver;
  resolveConfluencePageConnection?: ConfluencePageConnectionResolver;
  includeAiFields?: boolean;
  entryFieldValuesSnapshot?: DatabaseEntryFieldValuesSnapshot;
};

export type SerializedDatabaseBatch = {
  output: string;
  rowIdToEntryIdMappings: Map<string, string>;
};

function getContentFieldIds(
  database: Database,
  fieldIds: readonly string[] | undefined,
  includeAiFields: boolean
): readonly string[] {
  const fieldsToSerialize = fieldIds ?? database.fields;

  return includeAiFields
    ? fieldsToSerialize
    : fieldsToSerialize.filter(
        (fieldId) => database.fieldsById[fieldId].type !== FieldType.AiSummary
      );
}

export function serializeDatabaseInBatches(
  database: Database,
  selectedCells: SelectedCells,
  batchSize: number,
  options: SerializeDatabaseOptions = {}
): {
  batches: [SerializedDatabaseBatch, ...SerializedDatabaseBatch[]];
  warnings: Warnings;
} {
  const {
    currentViewId,
    entryIds,
    fieldIds,
    includeAiFields = false,
    entryFieldValuesSnapshot,
    resolveJiraIssueDetails,
    resolveConfluencePageConnection
  } = options;

  const warnings = new Warnings();
  const schema = serializeDatabaseSchema(database, warnings, {
    fieldIds: includeAiFields ? fieldIds : undefined,
    includeAiFields
  });
  const views = serializeDatabaseViews(database, warnings, currentViewId);
  const contentFieldIds = getContentFieldIds(database, fieldIds, includeAiFields);
  const contentBatches = serializeDatabaseContentsInBatches(database, warnings, {
    batchSize,
    entryIds,
    fieldIds: contentFieldIds,
    resolveJiraIssueDetails,
    resolveConfluencePageConnection,
    entryFieldValuesSnapshot
  });

  const toDatabaseBatch = ({
    output: contents,
    rowIdToEntryIdMappings
  }: (typeof contentBatches)[number]): SerializedDatabaseBatch => {
    let output = `${schema}\r\n\r\n${views}\r\n\r\n${contents}`;
    if (selectedCells.size > 0) {
      output += `\r\n\r\n${serializeSelectedCells(
        selectedCells,
        database,
        rowIdToEntryIdMappings
      )}`;
    }

    return { output, rowIdToEntryIdMappings };
  };
  const [firstContentBatch, ...remainingContentBatches] = contentBatches;

  return {
    batches: [
      toDatabaseBatch(firstContentBatch),
      ...remainingContentBatches.map(toDatabaseBatch)
    ],
    warnings
  };
}

export function serializeDatabase(
  database: Database,
  selectedCells: SelectedCells,
  options: SerializeDatabaseOptions = {}
) {
  const {
    currentViewId,
    entryIds,
    fieldIds,
    includeAiFields = false,
    entryFieldValuesSnapshot,
    resolveJiraIssueDetails,
    resolveConfluencePageConnection
  } = options;

  const warnings = new Warnings();
  let output = '';
  const rowIdToEntryIdMappings = new Map<string, string>();

  output += serializeDatabaseSchema(database, warnings, {
    fieldIds: includeAiFields ? fieldIds : undefined,
    includeAiFields
  });
  output += '\r\n\r\n';
  output += serializeDatabaseViews(database, warnings, currentViewId);
  output += '\r\n\r\n';
  const contentFieldIds = getContentFieldIds(database, fieldIds, includeAiFields);
  output += serializeDatabaseContents(
    database,
    warnings,
    rowIdToEntryIdMappings,
    entryIds,
    contentFieldIds,
    resolveJiraIssueDetails,
    resolveConfluencePageConnection,
    entryFieldValuesSnapshot
  );

  if (selectedCells.size > 0) {
    output += '\r\n\r\n';
    output += serializeSelectedCells(selectedCells, database, rowIdToEntryIdMappings);
  }

  return {
    output,
    warnings,
    rowIdToEntryIdMappings
  };
}
